import { useState, useMemo, useEffect } from 'react';
import { getInterestRate, calculatePersonalPayment } from '@atc/wallet-payment-fns';
import { defaultPaymentInfo } from 'axl-config';

/**
 * Custom hook for calculating and managing personal payment state
 * @param {Object} options - Configuration object
 * @param {number|string} options.price - Listing price for calculation
 * @param {Object} options.paymentsInfo - Payment details for calculation
 * @param {string} options.vehicleCondition - Vehicle condition for interest rate
 * @param {number} options.personalizedPayment - Specific payment amount to display
 * @param {boolean} options.shouldUpdatePayment - Whether to update payment calculations
 * @param {Function} options.handleFallbackPayment - Callback fired when payment is calculated
 * @returns {number} payment - The calculated or provided payment amount
 */
function usePersonalPayment(_ref) {
  let {
    price,
    paymentsInfo,
    vehicleCondition,
    personalizedPayment,
    shouldUpdatePayment = true,
    handleFallbackPayment = () => {}
  } = _ref;
  const [payment, setPayment] = useState(personalizedPayment || null);
  const interestRates = paymentsInfo?.interestRate || defaultPaymentInfo.interestRate;
  const interestRate = useMemo(() => getInterestRate(interestRates, vehicleCondition), [interestRates, vehicleCondition]);
  useEffect(() => {
    const handleUpdateMonthlyPayment = () => {
      const monthlyPayment = Math.max(0, calculatePersonalPayment(price, paymentsInfo || defaultPaymentInfo, interestRate)) || 0;
      setPayment(monthlyPayment);
      handleFallbackPayment({
        payment: monthlyPayment,
        rate: interestRate,
        term: paymentsInfo?.loanTerm || defaultPaymentInfo.loanTerm
      });
    };
    if (shouldUpdatePayment || payment === null) {
      if (personalizedPayment && typeof interestRates.custom !== 'number') {
        if (personalizedPayment !== payment) setPayment(personalizedPayment);
      } else {
        handleUpdateMonthlyPayment();
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [paymentsInfo, personalizedPayment, shouldUpdatePayment, handleFallbackPayment]);
  return payment;
}
export { usePersonalPayment };
